import styled from '@emotion/styled';
import { Button, Checkbox } from 'antd';
import { Confirm, ConfirmReturnRefund } from 'components/Refund';
import ForwardToAgent from 'components/Refund/ForwardToAgent';
import { Refuse } from 'components/Refund/Refuse';
import Image from 'components/SafeImage';
import { useGetPermission } from 'hooks/business/useNewPermisson';
import useModal from 'hooks/useModal';
import dayjs from 'lib/dayjs';
import { formatNumber } from 'utils/numberFormat';
import { useState, memo } from 'react';
import { useTranslate } from '@tolgee/react';
import { ReturnItem, ReturnOrderListItemV2, StatusHistory } from 'services/api/refund';
import Copy from 'views/Conversation/Sider/Copy';
import { useGetReasonLabel } from 'hooks/business/refundReason/useGetReasonLabel';
import CountDown from './CountDown';
import StatusHistoryModal, { useStatusLabel } from './StatusHistoryModal';
import useChatHistory from './useChatHIstory';

const Container = styled.section`
  padding: 0 12px;
  > * {
    padding: 12px 0;
  }
  > * + * {
    border-top: solid 1px #f0f2f5;
  }
`;

const BatchOperate = styled.div`
  display: flex;
  align-items: center;
  > * + * {
    margin-left: 12px;
  }
`;

const ItemWrapper = styled.div`
  display: flex;
  > * {
    flex: 0 0 auto;
  }
  > * + * {
    margin-left: 12px;
  }
`;

const Content = styled.div`
  flex: 1 0 0 !important;
  > section {
    display: flex;
    > div {
      flex: 1 0 0;
      > dl {
        display: flex;
        align-items: center;
        flex-wrap: wrap;
        margin: 0;
        line-height: 24px;
        > dt {
          font-weight: 400;
          color: #84868c;
          white-space: nowrap;
        }
        > dd {
          margin-bottom: 0;
        }
        button {
          padding-left: 0;
        }
        > span {
          margin: -4px 0 0 4px;
        }
      }
      & + div {
        margin-left: 12px;
      }
    }
  }
`;

const Operate = styled.div`
  align-self: center;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  width: 200px;
  margin-left: 24px;
`;

const DueTime = styled.dl`
  align-items: flex-start !important;
  > dd {
    color: var(--ant-error-color);
  }
`;

interface LazadaReturnItemsProps {
  returnOrder: ReturnOrderListItemV2;
  onRefresh: () => void;
}

function LazadaReturnItems(props: LazadaReturnItemsProps) {
  const { returnOrder, onRefresh } = props;

  const {
    internalStoreId,
    externalStoreId,
    returnSn,
    orderSn,
    unit,
    returnItemData = [],
    region,
  } = returnOrder;

  const { t } = useTranslate();

  const { getReasonLabel } = useGetReasonLabel();

  const operatable = useGetPermission()('操作弃单', {
    platform: 'LAZADA',
    region,
    storeId: Number(internalStoreId),
  } as any);

  const statusLabel = useStatusLabel();

  const [checkedList, setCheckedList] = useState<string[]>([]);

  const { visible, show, hide, meta } = useModal<StatusHistory[]>();

  const [openChatHistory, chatHistoryModal] = useChatHistory();

  if (returnItemData.length === 0) {
    return null;
  }

  const canOperate = ({ operate = [] }: ReturnItem) =>
    operate.filter((_) => _ !== 'STATUS_HISTORY').length > 0;

  const canOperateItemList = returnItemData.filter(canOperate);

  const canBatchOperate = canOperateItemList.length > 1;

  const checked = ({ returnOrderItemId }: ReturnItem) =>
    returnOrderItemId ? checkedList.includes(returnOrderItemId) : false;

  const batchOperate = (() => {
    if (!operatable || !canBatchOperate) {
      return null;
    }

    const allChecked = canOperateItemList.every(checked);

    const indeterminate = !allChecked && canOperateItemList.some(checked);

    const enable = (operate: Conversation.Trade.IReturnOperationType) => {
      return (
        checkedList.length > 0 &&
        checkedList.every((id) => {
          return canOperateItemList.some(({ returnOrderItemId, operate: operates = [] }) => {
            return returnOrderItemId === id && operates.includes(operate);
          });
        })
      );
    };

    return (
      <BatchOperate>
        <Checkbox
          checked={allChecked}
          indeterminate={indeterminate}
          onChange={({ target: { checked } }) =>
            setCheckedList(
              checked
                ? canOperateItemList
                    .map(({ returnOrderItemId = '' }) => returnOrderItemId)
                    .filter((_) => _)
                : [],
            )
          }
        />
        <Confirm
          externalStoreId={externalStoreId}
          externalTradeId={orderSn}
          returnSn={returnSn}
          onSuccess={onRefresh}
          operate="CONFIRM"
          platform="LAZADA"
          returnOrderItemId={checkedList}
          disabled={!enable('CONFIRM')}
          unit={unit}
          refundAmount={checkedList.reduce((res, id) => {
            const target = canOperateItemList.find(
              ({ returnOrderItemId }) => returnOrderItemId === id,
            );

            if (!target) {
              return res;
            }

            return res + (target.refundAmount || 0);
          }, 0)}
        />
        <ConfirmReturnRefund
          externalStoreId={externalStoreId}
          externalTradeId={orderSn}
          returnSn={returnSn}
          platform="LAZADA"
          returnOrderItemId={checkedList}
          disabled={!enable('CONFIRM_RETURN_REFUND')}
          onSucceed={onRefresh}
        />
        <Refuse
          internalStoreId={internalStoreId}
          externalStoreId={externalStoreId}
          returnSn={returnSn}
          returnOrderItemId={checkedList}
          onRefused={onRefresh}
          disabled={!enable('REFUSE')}
        />
      </BatchOperate>
    );
  })();

  const statusHistoryModal = meta && (
    <StatusHistoryModal visible={visible} onCancel={() => hide()} data={meta} />
  );

  const items = returnItemData.map((returnItem) => {
    const {
      itemImage: [imageSrc],
      itemName,
      sellerSku,
      returnOrderItemId = '',
      orderItemId,
      returnStatusHistory,
      itemPrice,
      refundAmount,
      reason,
      displayStatus,
      dueDateMs,
      operate = [],
      returnTrackingNumber,
    } = returnItem;

    const checkbox = (() => {
      if (!canBatchOperate) {
        return null;
      }

      if (operate.length === 0) {
        return <span />;
      }

      return (
        <Checkbox
          checked={checked(returnItem)}
          onChange={({ target: { checked } }) =>
            setCheckedList(
              checked
                ? [...checkedList, returnOrderItemId]
                : checkedList.filter((id) => id !== returnOrderItemId),
            )
          }
        />
      );
    })();

    const image = (
      <Image
        preview
        width={56}
        height={56}
        src={imageSrc}
        onClick={(e) => e.stopPropagation()}
        hasSmallPic="both"
      />
    );

    const title = <h4>{itemName}</h4>;

    const column1 = (
      <div>
        <dl>
          <dt>{t('ViewRefund.卖家SKU ID')}：</dt>
          <dd>{sellerSku}</dd>
          <Copy
            text={sellerSku}
            successMessage={t('trade.复制成功')}
            failedMessage={t('trade.复制失败')}
          />
        </dl>
        <dl>
          <dt>{t('ViewRefund.退货商品编号')}：</dt>
          <dd>{returnOrderItemId}</dd>
          <Copy
            text={returnOrderItemId}
            successMessage={t('trade.复制成功')}
            failedMessage={t('trade.复制失败')}
          />
        </dl>
        <dl>
          <dt>{t('ViewRefund.订单商品编号')}：</dt>
          <dd>{orderItemId || '-'}</dd>
          {orderItemId ? (
            <Copy
              text={orderItemId}
              successMessage={t('trade.复制成功')}
              failedMessage={t('trade.复制失败')}
            />
          ) : null}
        </dl>
        {returnTrackingNumber && (
          <dl>
            <dt>{t('物流单号')}：</dt>
            <dd>{returnTrackingNumber}</dd>
            <Copy
              text={returnTrackingNumber}
              successMessage={t('trade.复制成功')}
              failedMessage={t('trade.复制失败')}
            />
          </dl>
        )}

        {operate.includes('STATUS_HISTORY') && displayStatus && statusLabel[displayStatus] ? (
          <dl>
            <dt>{t('aide.状态')}：</dt>
            <dd>
              <Button type="link" onClick={() => show(returnStatusHistory)}>
                {statusLabel[displayStatus]}
              </Button>
            </dd>
          </dl>
        ) : null}
      </div>
    );

    const column2 = (
      <div>
        <dl>
          <dt>{t('ViewRefund.已付价格+运费')}：</dt>
          <dd>
            {unit}
            {formatNumber(itemPrice, '0,0.00')}
          </dd>
        </dl>
        <dl>
          <dt>{t('trade.退款金额')}：</dt>
          <dd>
            {unit}
            {formatNumber(refundAmount, '0,0.00')}
          </dd>
        </dl>
        <dl>
          <dt>{t('ViewRefund.退货原因')}：</dt>
          <dd>{getReasonLabel(reason || '-', 'LAZADA')}</dd>
        </dl>
        <dl>
          <dd>
            <Button type="link" onClick={() => openChatHistory(returnSn, returnOrderItemId)}>
              {t('ViewRefund.通讯历史')}
            </Button>
          </dd>
        </dl>
      </div>
    );

    const dueTime = Number(dueDateMs);

    const column3 = (() => {
      if (!displayStatus) {
        return null;
      }

      if (displayStatus !== 'REQUEST_INITIATE') {
        return <div>{statusLabel[displayStatus] || '-'}</div>;
      }

      if (dueTime) {
        return (
          <div>
            <DueTime>
              <dt>SLA：</dt>
              <dd>
                {dayjs(dueTime).format('YYYY-MM-DD HH:mm:ss')}
                {dueTime > Date.now() && <CountDown time={dueTime} />}
              </dd>
            </DueTime>
          </div>
        );
      }

      return null;
    })();

    const content = (
      <Content>
        {title}
        <section>
          {column1}
          {column2}
          {column3}
        </section>
      </Content>
    );

    const operateList = (() => {
      if (!operatable) {
        return null;
      }

      if (operate.filter((_) => _ !== 'STATUS_HISTORY').length === 0) {
        return <Operate />;
      }

      return (
        <Operate>
          {operate.map((_) => {
            if (_ === 'CONFIRM') {
              return (
                <Confirm
                  type="text"
                  externalStoreId={externalStoreId}
                  externalTradeId={orderSn}
                  returnSn={returnSn}
                  onSuccess={onRefresh}
                  operate="CONFIRM"
                  platform="LAZADA"
                  returnOrderItemId={[returnOrderItemId]}
                  unit={unit}
                  refundAmount={refundAmount}
                />
              );
            }

            if (_ === 'CONFIRM_RETURN_REFUND') {
              return (
                <ConfirmReturnRefund
                  externalStoreId={externalStoreId}
                  externalTradeId={orderSn}
                  returnSn={returnSn}
                  type="text"
                  platform="LAZADA"
                  returnOrderItemId={[returnOrderItemId]}
                  onSucceed={onRefresh}
                />
              );
            }

            if (_ === 'FORWARD_TO_AGENT') {
              return <ForwardToAgent type="text" />;
            }

            if (_ === 'REFUSE') {
              return (
                <Refuse
                  type="text"
                  internalStoreId={internalStoreId}
                  externalStoreId={externalStoreId}
                  returnSn={returnSn}
                  returnOrderItemId={[returnOrderItemId]}
                  onRefused={onRefresh}
                />
              );
            }

            return null;
          })}
        </Operate>
      );
    })();

    return (
      <ItemWrapper key={returnOrderItemId}>
        {checkbox}
        {image}
        {content}
        {operateList}
      </ItemWrapper>
    );
  });

  return (
    <Container>
      {batchOperate}
      {items}
      {statusHistoryModal}
      {chatHistoryModal}
    </Container>
  );
}

export default memo(LazadaReturnItems);
